import { useEffect, useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { accountApi } from "../../services/apiClient";
import { PageContent, PageHeader, PageShell } from "../../components/layout/PageShell";

const card = {
  background: "#fff",
  borderRadius: 12,
  border: "1px solid #e5e7eb",
  padding: 20,
  marginBottom: 16,
};

export default function PageAccount({ currentUser, onSessionRefresh, onToast, onBack }) {
  const [displayName, setDisplayName] = useState(currentUser?.display_name || "");
  const [profilePassword, setProfilePassword] = useState("");

  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");

  const [emailNew, setEmailNew] = useState("");
  const [emailPassword, setEmailPassword] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [emailStep, setEmailStep] = useState("idle");

  const [mfaOtpauth, setMfaOtpauth] = useState("");
  const [mfaStep, setMfaStep] = useState("idle");
  const [mfaConfirmCode, setMfaConfirmCode] = useState("");
  const [mfaDisablePassword, setMfaDisablePassword] = useState("");
  const [mfaDisableCode, setMfaDisableCode] = useState("");

  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setDisplayName(currentUser?.display_name || "");
  }, [currentUser?.id, currentUser?.display_name]);

  const mfaEnabled = !!currentUser?.mfa_enabled;

  const label = useMemo(
    () => ({
      display: "block",
      fontSize: 12,
      color: "#374151",
      marginBottom: 6,
      fontWeight: 600,
    }),
    []
  );

  const input = useMemo(
    () => ({
      width: "100%",
      maxWidth: 360,
      padding: "10px 12px",
      borderRadius: 8,
      border: "1px solid #d1d5db",
      marginBottom: 12,
      fontSize: 14,
    }),
    []
  );

  const saveProfile = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = await accountApi.updateProfile({
        display_name: displayName.trim(),
        current_password: profilePassword,
      });
      onToast?.("success", data?.message || "Profil enregistré.");
      setProfilePassword("");
      await onSessionRefresh?.();
    } catch (err) {
      onToast?.("error", err.message || "Erreur.");
    } finally {
      setBusy(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = await accountApi.updatePassword({
        current_password: pwCurrent,
        password: pwNew,
        password_confirmation: pwConfirm,
      });
      onToast?.("success", data?.message || "Mot de passe mis à jour.");
      setPwCurrent("");
      setPwNew("");
      setPwConfirm("");
    } catch (err) {
      onToast?.("error", err.message || "Erreur.");
    } finally {
      setBusy(false);
    }
  };

  const requestEmailCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = await accountApi.requestEmailChangeCode({
        new_email: emailNew.trim(),
        current_password: emailPassword,
      });
      onToast?.("success", data?.message || "Code envoyé.");
      setEmailStep("confirm");
      setEmailCode("");
    } catch (err) {
      onToast?.("error", err.message || "Erreur.");
    } finally {
      setBusy(false);
    }
  };

  const confirmEmail = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = await accountApi.confirmEmailChange({
        new_email: emailNew.trim(),
        code: emailCode.trim(),
      });
      onToast?.("success", data?.message || "E-mail mis à jour.");
      setEmailStep("idle");
      setEmailNew("");
      setEmailPassword("");
      setEmailCode("");
      await onSessionRefresh?.();
    } catch (err) {
      onToast?.("error", err.message || "Erreur.");
    } finally {
      setBusy(false);
    }
  };

  const startMfa = async () => {
    setBusy(true);
    try {
      const data = await accountApi.mfaSetup();
      setMfaOtpauth(data.otpauth_url || "");
      setMfaStep("scan");
      setMfaConfirmCode("");
      onToast?.("success", data?.message || "Scannez le QR code puis confirmez.");
    } catch (err) {
      onToast?.("error", err.message || "Erreur.");
    } finally {
      setBusy(false);
    }
  };

  const confirmMfa = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = await accountApi.mfaConfirm(mfaConfirmCode.trim());
      onToast?.("success", data?.message || "2FA activée.");
      setMfaStep("idle");
      setMfaOtpauth("");
      setMfaConfirmCode("");
      await onSessionRefresh?.();
    } catch (err) {
      onToast?.("error", err.message || "Code incorrect.");
    } finally {
      setBusy(false);
    }
  };

  const disableMfa = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = await accountApi.mfaDisable(mfaDisablePassword, mfaDisableCode.trim());
      onToast?.("success", data?.message || "2FA désactivée.");
      setMfaDisablePassword("");
      setMfaDisableCode("");
      await onSessionRefresh?.();
    } catch (err) {
      onToast?.("error", err.message || "Erreur.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageShell>
      <PageHeader title="Mon compte" onBack={onBack} backLabel="← Retour" />

      <PageContent>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <p style={{ color: "#6b7280", fontSize: 14, marginBottom: 20, lineHeight: 1.6 }}>
            E-mail actuel : <strong>{currentUser?.email}</strong>
            {mfaEnabled ? (
              <span style={{ marginLeft: 8, color: "#059669", fontWeight: 700 }}>
                · 2FA active
              </span>
            ) : null}
          </p>

      <div style={card}>
        <h2 style={{ marginTop: 0, fontSize: 16, color: "#111827" }}>Nom affiché</h2>
        <p style={{ fontSize: 13, color: "#6b7280", marginTop: 0 }}>Vérifiez votre identité avec votre mot de passe pour modifier ce libellé.</p>
        <form onSubmit={saveProfile}>
          <label style={label}>Nom affiché</label>
          <input style={input} value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Ex. Marie Dupont" />
          <label style={label}>Mot de passe actuel</label>
          <input type="password" style={input} value={profilePassword} onChange={(e) => setProfilePassword(e.target.value)} autoComplete="current-password" />
          <button type="submit" disabled={busy || !profilePassword} style={{ padding: "10px 18px", borderRadius: 8, border: "none", background: "#2563eb", color: "#fff", fontWeight: 700, cursor: "pointer", opacity: busy ? 0.65 : 1 }}>
            Enregistrer le profil
          </button>
        </form>
      </div>

      <div style={card}>
        <h2 style={{ marginTop: 0, fontSize: 16, color: "#111827" }}>Mot de passe</h2>
        <p style={{ fontSize: 13, color: "#6b7280", marginTop: 0 }}>Le changement n’est accepté que si le mot de passe actuel est correct.</p>
        <form onSubmit={savePassword}>
          <label style={label}>Mot de passe actuel</label>
          <input type="password" style={input} value={pwCurrent} onChange={(e) => setPwCurrent(e.target.value)} autoComplete="current-password" />
          <label style={label}>Nouveau mot de passe (min. 8 caractères)</label>
          <input type="password" style={input} value={pwNew} onChange={(e) => setPwNew(e.target.value)} autoComplete="new-password" />
          <label style={label}>Confirmation</label>
          <input type="password" style={input} value={pwConfirm} onChange={(e) => setPwConfirm(e.target.value)} autoComplete="new-password" />
          <button type="submit" disabled={busy || !pwCurrent || pwNew.length < 8} style={{ padding: "10px 18px", borderRadius: 8, border: "none", background: "#1d4ed8", color: "#fff", fontWeight: 700, cursor: "pointer", opacity: busy ? 0.65 : 1 }}>
            Mettre à jour le mot de passe
          </button>
        </form>
      </div>

      <div style={card}>
        <h2 style={{ marginTop: 0, fontSize: 16, color: "#111827" }}>Adresse e-mail</h2>
        <p style={{ fontSize: 13, color: "#6b7280", marginTop: 0 }}>
          Un code est envoyé à votre <strong>adresse actuelle</strong> pour confirmer la demande. Saisissez ensuite le code et la nouvelle adresse pour finaliser.
        </p>
        {emailStep === "idle" ? (
          <form onSubmit={requestEmailCode}>
            <label style={label}>Nouvelle adresse e-mail</label>
            <input type="email" style={input} value={emailNew} onChange={(e) => setEmailNew(e.target.value)} autoComplete="off" />
            <label style={label}>Mot de passe actuel</label>
            <input type="password" style={input} value={emailPassword} onChange={(e) => setEmailPassword(e.target.value)} autoComplete="current-password" />
            <button type="submit" disabled={busy || !emailNew || !emailPassword} style={{ padding: "10px 18px", borderRadius: 8, border: "none", background: "#7c3aed", color: "#fff", fontWeight: 700, cursor: "pointer", opacity: busy ? 0.65 : 1 }}>
              Envoyer le code de confirmation
            </button>
          </form>
        ) : (
          <form onSubmit={confirmEmail}>
            <p style={{ fontSize: 13, color: "#374151" }}>Code reçu par e-mail (6 chiffres) pour valider le changement vers <strong>{emailNew}</strong>.</p>
            <label style={label}>Code</label>
            <input style={input} value={emailCode} onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" />
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button type="submit" disabled={busy || emailCode.trim().length !== 6} style={{ padding: "10px 18px", borderRadius: 8, border: "none", background: "#7c3aed", color: "#fff", fontWeight: 700, cursor: "pointer" }}>
                Confirmer la nouvelle adresse
              </button>
              <button type="button" onClick={() => { setEmailStep("idle"); setEmailCode(""); }} style={{ padding: "10px 18px", borderRadius: 8, border: "1px solid #d1d5db", background: "#fff", cursor: "pointer" }}>
                Annuler
              </button>
            </div>
          </form>
        )}
      </div>

      <div style={card}>
        <h2 style={{ marginTop: 0, fontSize: 16, color: "#111827" }}>Authentification à deux facteurs (Google Authenticator)</h2>
        {!mfaEnabled ? (
          <>
            {mfaStep === "idle" ? (
              <button type="button" disabled={busy} onClick={startMfa} style={{ padding: "10px 18px", borderRadius: 8, border: "none", background: "#0d9488", color: "#fff", fontWeight: 700, cursor: "pointer" }}>
                Configurer la 2FA
              </button>
            ) : (
              <form onSubmit={confirmMfa}>
                <p style={{ fontSize: 13, color: "#374151" }}>Scannez ce QR code, puis entrez le code à 6 chiffres pour confirmer.</p>
                <div style={{ margin: "16px 0", padding: 16, background: "#f9fafb", borderRadius: 12, display: "inline-block" }}>
                  {mfaOtpauth ? <QRCodeSVG value={mfaOtpauth} size={200} level="M" /> : null}
                </div>
                <div>
                  <label style={label}>Code de vérification</label>
                  <input style={input} value={mfaConfirmCode} onChange={(e) => setMfaConfirmCode(e.target.value.replace(/\D/g, "").slice(0, 8))} inputMode="numeric" placeholder="123456" />
                </div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <button type="submit" disabled={busy || mfaConfirmCode.trim().length < 6} style={{ padding: "10px 18px", borderRadius: 8, border: "none", background: "#0d9488", color: "#fff", fontWeight: 700, cursor: "pointer" }}>
                    Activer la 2FA
                  </button>
                  <button type="button" onClick={() => { setMfaStep("idle"); setMfaOtpauth(""); }} style={{ padding: "10px 18px", borderRadius: 8, border: "1px solid #d1d5db", background: "#fff", cursor: "pointer" }}>
                    Annuler
                  </button>
                </div>
              </form>
            )}
          </>
        ) : (
          <form onSubmit={disableMfa}>
            <p style={{ fontSize: 13, color: "#6b7280" }}>Pour désactiver : mot de passe actuel et un code valide de votre application.</p>
            <label style={label}>Mot de passe</label>
            <input type="password" style={input} value={mfaDisablePassword} onChange={(e) => setMfaDisablePassword(e.target.value)} autoComplete="current-password" />
            <label style={label}>Code 2FA</label>
            <input style={input} value={mfaDisableCode} onChange={(e) => setMfaDisableCode(e.target.value.replace(/\D/g, "").slice(0, 8))} inputMode="numeric" />
            <button type="submit" disabled={busy || !mfaDisablePassword || mfaDisableCode.trim().length < 6} style={{ padding: "10px 18px", borderRadius: 8, border: "none", background: "#b91c1c", color: "#fff", fontWeight: 700, cursor: "pointer" }}>
              Désactiver la 2FA
            </button>
          </form>
        )}
      </div>
        </div>
      </PageContent>
    </PageShell>
  );
}
